import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { LineChart } from "@mui/x-charts/LineChart";
import PropTypes from "prop-types";
import * as React from "react";
import axiosInstance from "../utils/axiosInstance";


function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

AreaGradient.propTypes = {
  color: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

export default function UserActivityChart() {
  const theme = useTheme();
  const [dates, setDates] = React.useState([]);
  const [count, setCount] = React.useState([]);

  const colorPalette = [
    theme.palette.primary.light,
    theme.palette.primary.main,
    theme.palette.primary.dark,
  ];

  const fetchUserActivitySummary = React.useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/user-activity/summary/30`,
      );

      if (response.status === 200) {
        const dates = Object.keys(response.data);
        const count = Object.values(response.data).map(Number);
        setDates(dates);
        setCount(count);
      }
    } catch (error) {}
  }, []);

  React.useEffect(() => {
    fetchUserActivitySummary();
  }, [fetchUserActivitySummary]);

  return (
    <Card variant="outlined">
      <CardContent>

        <Typography component="h2" variant="subtitle2" gutterBottom>
          User Activity
        </Typography>

        <Stack sx={{ justifyContent: "space-between" }}>
          <Stack
            direction="row"
            sx={{
              alignContent: { xs: "center", sm: "flex-start" },
              alignItems: "center",
              gap: 1,
            }}
          >
            {count && (
              <Typography variant="h4" component="p">
                {count.reduce(
                  (total, value) => total + value,
                  0,
                )}
              </Typography>
            )}
          </Stack>

          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {`User activity per day for the last 30 days`}
          </Typography>
        </Stack>
        {count && dates && (
          <LineChart
            colors={colorPalette}
            xAxis={[
              {
                scaleType: "point",
                data: dates,
                tickInterval: (index, i) => (i + 1) % 5 === 0,
              },
            ]}
            series={[
              {
                id: "direct",
                label: "User Activity",
                showMark: false,
                curve: "linear",
                stack: "total",
                area: true,
                stackOrder: "ascending",
                data: count,
              },
            ]}
            height={250}
            margin={{ left: 50, right: 20, top: 20, bottom: 20 }}
            grid={{ horizontal: true }}
            sx={{
              "& .MuiAreaElement-series-organic": {
                fill: "url('#organic')",
              },
              "& .MuiAreaElement-series-referral": {
                fill: "url('#referral')",
              },
              "& .MuiAreaElement-series-direct": {
                fill: "url('#direct')",
              },
            }}
            slotProps={{
              legend: {
                hidden: true,
              },
            }}
          >
            <AreaGradient color={theme.palette.primary.dark} id="organic" />
            <AreaGradient color={theme.palette.primary.main} id="referral" />
            <AreaGradient color={theme.palette.primary.light} id="direct" />
          </LineChart>
        )}
      </CardContent>
    </Card>
  );
}
